import React from 'react';

import useIsAdminView from 'hooks/useIsAdminView';
import { useTranslation } from 'react-i18next';
import { useBillingPaymentSettingsForChannel } from 'lane-shared/domains/billingPayments/hooks';
import useUserLocale from 'hooks/useUserLocale';
import { getPricing, type PricingParams } from 'domains-billing-calculation';
import { useBpAdminFeeChargeCodeEnabled } from 'react-hooks';

import {
  ProductItem,
  ChargePartial,
  TaxPolicy,
} from 'lane-shared/domains/billingPayments/types';
import { formatCurrency } from 'domains-billing-currency';
import {
  currencyToPaymentCurrency,
  graphQLToCurrency,
} from 'domains-billing-web-mappers';

import { Table, Column } from 'design-system-web';

export function ItemsTable({
  channelId,
  charge,
}: {
  channelId: string;
  charge: ChargePartial;
}) {
  const { t } = useTranslation();
  const [isAdminView] = useIsAdminView();
  const currency = graphQLToCurrency(charge.currency);
  const formatCurrencyAmount = formatCurrency(currency, {
    currencyDisplay: 'narrowSymbol',
  });
  const chargeIsVoided = charge.status === 'VOIDED';
  const locale = useUserLocale();

  const { taxDetails } = useBillingPaymentSettingsForChannel({
    channelId,
  });
  const taxInclusive = taxDetails?.taxPolicy === TaxPolicy.INCLUSIVE;
  const isAdminFeeFeatureEnabled = useBpAdminFeeChargeCodeEnabled();

  let hasAdminFee = false;
  let hasAdminFeeTax = false;

  const items = charge.items.map(item => {
    const params: PricingParams = {
      currency: currencyToPaymentCurrency(charge.currency),
      locale,
      priceComponents: item.product,
      ...(isAdminFeeFeatureEnabled
        ? { inclusivePricing: taxInclusive, isAdminFeeFeatureEnabled: true }
        : {}),
    };

    const {
      grossTax,
      grossMarkup,
      grossAdminFee,
      grossAdminFeeTax,
      listPrice,
      netPrice,
    } = getPricing(params);

    if ((grossAdminFee ?? 0) > 0) hasAdminFee = true;
    if ((grossAdminFeeTax ?? 0) > 0) hasAdminFeeTax = true;

    const rateWithMarkup = (item.product.amount ?? 0) + (grossMarkup ?? 0);

    const legacyNetPrice = taxInclusive ? netPrice : listPrice;
    const finalNetPrice = isAdminFeeFeatureEnabled ? netPrice : legacyNetPrice;

    const product = {
      ...item.product,
      grossTax,
      grossMarkup,
      grossAdminFee,
      grossAdminFeeTax,
      listPrice,
      rateWithMarkup,
      netPrice: finalNetPrice,
    };

    return { product, quantity: item.quantity } as ProductItem;
  });

  const voidedCell = (value: any) => <s>{value}</s>;
  const normalCell = (value: any) => <span>{value}</span>;

  const renderAmountCell = (cell: number) => {
    const value = formatCurrencyAmount(cell);

    return chargeIsVoided ? voidedCell(value) : normalCell(value);
  };

  const renderTotalPriceCell = (cell: number, row: ProductItem) => {
    const total = cell * row.quantity;
    const value = formatCurrencyAmount(total);

    return chargeIsVoided ? voidedCell(value) : normalCell(value);
  };

  const itemColumns: Column<any>[] = [
    {
      key: 'product.name',
      header: t('abp.productsServices.table.productName'),
      renderCell: chargeIsVoided ? voidedCell : normalCell,
    },
    {
      key: 'quantity',
      header: t('abp.productsServices.table.quantity'),
      renderCell: chargeIsVoided ? voidedCell : normalCell,
    },
  ];

  if (isAdminFeeFeatureEnabled) {
    const showAdminFee = isAdminView || hasAdminFee;
    const showAdminFeeTax = isAdminView || hasAdminFeeTax;

    itemColumns.push({
      key: 'product.rateWithMarkup',
      header: t('abp.productsServices.table.rate'),
      renderCell: renderAmountCell,
    });

    if (showAdminFee) {
      itemColumns.push({
        key: 'product.grossAdminFee',
        header: t('abp.productsServices.table.adminFee'),
        renderCell: renderAmountCell,
      });
    }

    if (showAdminFeeTax) {
      itemColumns.push({
        key: 'product.grossAdminFeeTax',
        header: t('abp.productsServices.table.adminFeeTax'),
        renderCell: renderAmountCell,
      });
    }
  } else if (isAdminView) {
    itemColumns.push(
      {
        key: 'product.amount',
        header: t('abp.productsServices.table.rate'),
        renderCell: renderAmountCell,
      },
      {
        key: 'product.grossMarkup',
        header: t('abp.productsServices.table.markup'),
        renderCell: renderAmountCell,
      }
    );
  } else {
    itemColumns.push({
      key: 'product.listPrice',
      header: t('abp.productsServices.table.price'),
      renderCell: renderAmountCell,
    });
  }

  itemColumns.push(
    {
      key: 'product.grossTax',
      header: taxDetails?.taxName || t('abp.productsServices.table.tax'),
      renderCell: renderAmountCell,
    },
    {
      key: 'product.netPrice',
      header: t('abp.productsServices.table.amount'),
      renderCell: renderTotalPriceCell,
    }
  );

  return (
    <div>
      {items.length > 0 && (
        <Table columns={itemColumns} data={items} disableSorting />
      )}
    </div>
  );
}
